import { isDesktopHost, waitForDesktopElectronApi } from '@/lib/desktop-host'

const DESKTOP_RUNTIME_CACHE_KEY = 'losi.desktop.runtime-config.v2'
const DESKTOP_RUNTIME_BACKGROUND_REFRESH_INTERVAL_MS = 30_000
const DESKTOP_RUNTIME_BRIDGE_TIMEOUT_MS = 10000
const DESKTOP_RUNTIME_LOAD_TIMEOUT_MS = 12000

let runtimeConfigMemoryCache: DesktopRuntimeSnapshot | null = null
let runtimeConfigRequest: Promise<DesktopRuntimeSnapshot | null> | null = null
let runtimeConfigRefreshRequest: Promise<DesktopRuntimeSnapshot | null> | null = null
let lastRuntimeRefreshStartedAt = 0

function sanitizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function sanitizeBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value
  }

  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  return fallback
}

function sanitizeStringArray(value: unknown): string[] {
  const next: string[] = []
  const seen = new Set<string>()

  for (const item of Array.isArray(value) ? value : []) {
    const normalized = sanitizeString(item)
    if (!normalized || seen.has(normalized)) {
      continue
    }

    seen.add(normalized)
    next.push(normalized)
  }

  return next
}

function normalizeDesktopRuntimeModelOption(value: unknown): DesktopRuntimeModelOption | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const input = value as Record<string, unknown>
  const id = sanitizeString(input.id)
  if (!id) {
    return null
  }

  return {
    id,
    label: sanitizeString(input.label || input.name) || id,
    ownedBy: sanitizeString(input.ownedBy || input.owned_by),
    defaultToolIds: sanitizeStringArray(input.defaultToolIds || input.default_tool_ids),
    toolsSupported: sanitizeBoolean(input.toolsSupported, sanitizeBoolean(input.tools, false)),
    tools: sanitizeBoolean(input.tools, sanitizeBoolean(input.toolsSupported, false)),
    imageInput: sanitizeBoolean(input.imageInput, sanitizeBoolean(input.image_input, false)),
    image_input: sanitizeBoolean(input.image_input, sanitizeBoolean(input.imageInput, false)),
    supportsStreaming: sanitizeBoolean(
      input.supportsStreaming,
      sanitizeBoolean(input.supports_streaming, false)
    ),
    supports_streaming: sanitizeBoolean(
      input.supports_streaming,
      sanitizeBoolean(input.supportsStreaming, false)
    ),
    tier: sanitizeString(input.tier),
    badge: sanitizeString(input.badge)
  }
}

function normalizeDesktopRuntimeTool(value: unknown): DesktopRuntimeTool | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const input = value as Record<string, unknown>
  const id = sanitizeString(input.id)
  if (!id) {
    return null
  }

  return {
    id,
    name: sanitizeString(input.name) || id,
    description: sanitizeString(input.description),
    sharing: sanitizeString(input.sharing),
    badge: sanitizeString(input.badge),
    additionalBadge: sanitizeString(input.additionalBadge),
    isNew: sanitizeBoolean(input.isNew, false)
  }
}

function normalizeDesktopRuntimeSnapshot(value: unknown): DesktopRuntimeSnapshot | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const input = value as Record<string, unknown>
  const model = sanitizeString(input.model)
  const models = sanitizeStringArray(input.models)
  const desktopDeviceId = sanitizeString(input.desktopDeviceId || input.desktop_device_id)
  const modelOptions = (Array.isArray(input.modelOptions) ? input.modelOptions : [])
    .map(normalizeDesktopRuntimeModelOption)
    .filter((option): option is DesktopRuntimeModelOption => option !== null)
  const tools = (Array.isArray(input.tools) ? input.tools : [])
    .map(normalizeDesktopRuntimeTool)
    .filter((tool): tool is DesktopRuntimeTool => tool !== null)

  if (
    !model &&
    !desktopDeviceId &&
    modelOptions.length === 0 &&
    models.length === 0 &&
    tools.length === 0
  ) {
    return null
  }

  return {
    model,
    models,
    modelOptions,
    tools,
    defaultToolIds: sanitizeStringArray(input.defaultToolIds),
    desktopDeviceId,
    runtimeSource: sanitizeString(input.runtimeSource),
    runtimeSyncedAt: sanitizeString(input.runtimeSyncedAt),
    runtimeStale: sanitizeBoolean(input.runtimeStale, false),
    runtimeSyncError: sanitizeString(input.runtimeSyncError),
    diagnostics:
      input.diagnostics && typeof input.diagnostics === 'object' ? input.diagnostics : undefined
  }
}

function persistDesktopRuntimeConfig(snapshot: DesktopRuntimeSnapshot | null) {
  if (typeof window === 'undefined') {
    return
  }

  if (!snapshot) {
    localStorage.removeItem(DESKTOP_RUNTIME_CACHE_KEY)
    return
  }

  localStorage.setItem(DESKTOP_RUNTIME_CACHE_KEY, JSON.stringify(snapshot))
}

function cacheDesktopRuntimeConfig(
  snapshot: DesktopRuntimeSnapshot | null
): DesktopRuntimeSnapshot | null {
  runtimeConfigMemoryCache = snapshot
  persistDesktopRuntimeConfig(snapshot)
  return snapshot
}

function readStoredDesktopRuntimeConfig(): DesktopRuntimeSnapshot | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return normalizeDesktopRuntimeSnapshot(
      JSON.parse(localStorage.getItem(DESKTOP_RUNTIME_CACHE_KEY) || 'null')
    )
  } catch {
    return null
  }
}

export function getCachedDesktopRuntimeConfig(): DesktopRuntimeSnapshot | null {
  if (runtimeConfigMemoryCache) {
    return runtimeConfigMemoryCache
  }

  const stored = readStoredDesktopRuntimeConfig()
  if (stored) {
    runtimeConfigMemoryCache = stored
  }

  return runtimeConfigMemoryCache
}

function shouldStartBackgroundRefresh(): boolean {
  return Date.now() - lastRuntimeRefreshStartedAt >= DESKTOP_RUNTIME_BACKGROUND_REFRESH_INTERVAL_MS
}

async function withRuntimeTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  fallback: T
): Promise<T> {
  let timeoutId: number | null = null

  try {
    return await Promise.race([
      promise,
      new Promise<T>((resolve) => {
        timeoutId = window.setTimeout(() => resolve(fallback), timeoutMs)
      })
    ])
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }
}

async function invokeDesktopRuntimeConfig(
  mode: 'get' | 'refresh',
  options: {
    allowStale?: boolean
    includeFallback?: boolean
    force?: boolean
  } = {}
): Promise<DesktopRuntimeSnapshot | null> {
  if (!isDesktopHost()) {
    return null
  }

  const electronAPI = await waitForDesktopElectronApi({
    timeoutMs: DESKTOP_RUNTIME_BRIDGE_TIMEOUT_MS
  })
  const desktopRuntimeApi = electronAPI?.desktop?.runtime
  const loader =
    mode === 'refresh' && typeof desktopRuntimeApi?.refresh === 'function'
      ? () =>
          desktopRuntimeApi.refresh?.({
            allowStale: options.allowStale !== false,
            includeFallback: options.includeFallback !== false,
            force: options.force === true
          })
      : mode === 'refresh' && typeof electronAPI?.refreshChatRuntimeConfig === 'function'
        ? () =>
            electronAPI.refreshChatRuntimeConfig?.({
              allowStale: options.allowStale !== false,
              includeFallback: options.includeFallback !== false,
              force: options.force === true
            })
        : typeof desktopRuntimeApi?.getConfig === 'function'
          ? () => desktopRuntimeApi.getConfig?.()
          : typeof electronAPI?.getChatRuntimeConfig === 'function'
            ? () => electronAPI.getChatRuntimeConfig?.()
            : null

  if (!loader) {
    return null
  }

  try {
    const snapshot = normalizeDesktopRuntimeSnapshot(
      await withRuntimeTimeout(
        Promise.resolve(loader()),
        DESKTOP_RUNTIME_LOAD_TIMEOUT_MS,
        null
      )
    )
    if (snapshot) {
      return cacheDesktopRuntimeConfig(snapshot)
    }
  } catch (error) {
    console.warn(`Failed to ${mode} desktop runtime config:`, error)
  }

  return getCachedDesktopRuntimeConfig()
}

export function refreshDesktopRuntimeConfig(
  options: {
    allowStale?: boolean
    includeFallback?: boolean
    force?: boolean
  } = {}
): Promise<DesktopRuntimeSnapshot | null> {
  if (!isDesktopHost()) {
    return Promise.resolve(null)
  }

  if (runtimeConfigRefreshRequest) {
    return runtimeConfigRefreshRequest
  }

  lastRuntimeRefreshStartedAt = Date.now()
  runtimeConfigRefreshRequest = invokeDesktopRuntimeConfig('refresh', options).finally(() => {
    runtimeConfigRefreshRequest = null
  })

  return runtimeConfigRefreshRequest
}

export async function getDesktopRuntimeConfig(
  options: {
    backgroundRefresh?: boolean
    allowStale?: boolean
    includeFallback?: boolean
    forceRefresh?: boolean
  } = {}
): Promise<DesktopRuntimeSnapshot | null> {
  if (!isDesktopHost()) {
    return null
  }

  const backgroundRefresh = options.backgroundRefresh !== false

  if (!options.forceRefresh) {
    const cached = getCachedDesktopRuntimeConfig()
    if (cached) {
      if (backgroundRefresh && shouldStartBackgroundRefresh()) {
        void refreshDesktopRuntimeConfig({
          allowStale: options.allowStale,
          includeFallback: options.includeFallback,
          force: false
        })
      }

      return cached
    }
  }

  if (!runtimeConfigRequest || options.forceRefresh) {
    lastRuntimeRefreshStartedAt = Date.now()
    runtimeConfigRequest = invokeDesktopRuntimeConfig('get', options).finally(() => {
      runtimeConfigRequest = null
    })
  }

  const runtimeConfig = await runtimeConfigRequest

  if (runtimeConfig && backgroundRefresh && shouldStartBackgroundRefresh()) {
    void refreshDesktopRuntimeConfig({
      allowStale: options.allowStale,
      includeFallback: options.includeFallback,
      force: false
    })
  }

  return runtimeConfig
}
